import { FC, Fragment, memo } from 'react';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { FilterOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/FilterOutlined';
import classNames from 'classnames';

import Sort from '../../sort/Sort';
import Pagination from '../../pagination/Pagination';

import styles from './Controls.module.scss';
import PrimaryFacet from '../../facets/primary-facet/PrimaryFacet';
import { useControlsData } from './data/useControlsData';
import PlpViewTypeSelector from '../../plp-view-type-selector/PlpViewTypeSelector';
import PlpCardSizeSelector from '../../plp-card-size-selector/PlpCardSizeSelector';
import ItemCount from '../../item-count/ItemCount';
import HorizontalFilterNav from '../../horizontal-filter-nav/HorizontalFilterNav';
import { AppliedFilter } from '../../facets/applied-filters/AppliedFilters';
import ClearFilters from '../../facets/clear-filters/ClearFilters';
import { SelectedFacetValue } from '../../../utils/types/types';
import { getNumberOfFilters } from '../../facets/applied-filters/helpers';

type ControlsProps = {
  current?: number;
  handleFilterDrawerClick?: (position: 'left' | 'right') => void;
  handlePrimaryFacetClick?: () => void;
  handleIndividualFacetClick?: (dimensionName: string) => void;
  numberOfFiltersToShowClear: number;
  pages?: number;
  primaryFacetDisplayName?: string;
  sortBy?: string;
  inView?: boolean;
};

const Controls: FC<ControlsProps> = ({
  current = 1,
  handleFilterDrawerClick,
  handlePrimaryFacetClick,
  handleIndividualFacetClick,
  numberOfFiltersToShowClear,
  pages = 1,
  primaryFacetDisplayName,
  sortBy = '',
  inView = true,
}) => {
  const activePage = current;
  const {
    experiences,
    facetContext,
    plpViewType,
    productContext,
    searchContext,
  } = useControlsData();

  const {
    isHideTopPaginationFlagOn = false,
    isProductCardSizeFlagOn,
    isReplaceTopPaginationFlagOn = false,
    isTabletEnhancementsFlagOn,
    isSingleColumnLayout,
  } = experiences;
  const { baseFacetId, isMoreFiltersButton } = facetContext;

  const { hasPlpViewTypeSelection } = plpViewType;

  const { totalNumberOfResults } = productContext;

  const testId = isMoreFiltersButton ? 'more-filters-button' : 'filters-button';

  const { filter } = searchContext;

  const numberOfFilters = getNumberOfFilters(filter);

  if (isSingleColumnLayout) {
    const hasAppliedFilters = filter && Object.keys(filter).length > 0;
    const showClearFilters = numberOfFiltersToShowClear <= numberOfFilters;
    const appliedFilterNames = hasAppliedFilters
      ? [].concat(...Object.values(filter))
      : [];
    if (appliedFilterNames.length) {
      appliedFilterNames.sort((filterA, filterB) =>
        filterA.name.toLowerCase().localeCompare(filterB.name.toLowerCase())
      );
    }
    return (
      <Fragment>
        <div className={styles['single-column-controls']}>
          <SecondaryButton
            className={classNames(styles['filter-drawer-button'], {
              [styles['filter-drawer-tablet']]: isTabletEnhancementsFlagOn,
            })}
            data-testid={testId}
            onClick={() => handleFilterDrawerClick('left')}
          >
            <span className={styles.filterIcon} aria-hidden="true">
              <FilterOutlined title="Apply filters" size="medium" />
            </span>
            Filter
            {numberOfFilters > 0 && (
              <Fragment>&nbsp;({numberOfFilters})</Fragment>
            )}
          </SecondaryButton>

          <PlpCardSizeSelector inView={inView} />

          <Sort sort={sortBy} />

          <div className={styles['align-right']}>
            <HorizontalFilterNav
              handleIndividualFacetClick={handleIndividualFacetClick}
              onAllFiltersClick={() => handleFilterDrawerClick('right')}
            />
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles['applied-filters-container']}>
            <div className={styles['applied-filters-contents']}>
              <div className={styles['clear-filters']}>
                <ClearFilters
                  baseFacetId={baseFacetId}
                  isHorizontalFilters={true}
                  query={{ page: undefined }}
                  show={showClearFilters}
                />
              </div>
              {appliedFilterNames.map((value: SelectedFacetValue) => (
                <div key={value.name} className={classNames(styles.filter)}>
                  <AppliedFilter {...value} label={value.name} />
                </div>
              ))}
            </div>
          </div>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SecondaryButton
        className={classNames(styles['filter-drawer-button'], {
          [styles['filter-drawer-tablet']]: isTabletEnhancementsFlagOn,
        })}
        data-testid={testId}
        onClick={handleFilterDrawerClick}
      >
        <span className={styles.filterIcon} aria-hidden="true">
          <FilterOutlined title="Apply filters" size="medium" />
        </span>
        Filter
        {numberOfFilters > 0 && <Fragment>&nbsp;({numberOfFilters})</Fragment>}
      </SecondaryButton>

      {isTabletEnhancementsFlagOn && primaryFacetDisplayName && (
        <PrimaryFacet
          className="tablet"
          handlePrimaryFacetClick={handlePrimaryFacetClick}
          primaryFacetDisplayName={primaryFacetDisplayName}
        />
      )}

      <Sort sort={sortBy} />

      <div
        className={classNames(styles['align-right'], {
          [styles['align-right--viewSelector']]:
            hasPlpViewTypeSelection || isProductCardSizeFlagOn,
        })}
      >
        {!isHideTopPaginationFlagOn && (
          <>
            <Pagination
              aria-label="Product listing page pagination"
              current={activePage}
              pages={pages}
              hidePaginationAbove768px={isReplaceTopPaginationFlagOn}
            />
            {isReplaceTopPaginationFlagOn && (
              <ItemCount totalNumberOfResults={totalNumberOfResults} />
            )}
          </>
        )}

        {hasPlpViewTypeSelection && (
          <PlpViewTypeSelector className="inControls" name="inControls" />
        )}

        {isProductCardSizeFlagOn && <PlpCardSizeSelector inView={inView} />}
      </div>
    </Fragment>
  );
};

export default memo(Controls);
